import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useNotification } from '../common/components/notification/NotificationContext';
import { useFileDownloadMutation } from '../generated/graphql';
import {
  writeFileInDirectory,
  writeFileInElectron,
} from '../common/utils/fileWritingUtils';
import { useIsElectron, useElectronAPI } from './ElectronContext';
import { useTransferSidebar } from './TransferSidebarContext';
import {
  assertSafeRelativePath,
  verifyDownloadedFile,
} from '../common/utils/fileIntegrity';
import { copyDirectory } from '../common/utils/copyDirectory';
import {
  createTransferDiagnostics,
  reportTransferError,
  type DiagnosticFile,
  type TransferDiagnosticContext,
} from '@/services/transferDiagnostics';

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum FileJobStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  COMPLETE = 'complete',
  ERROR = 'error',
}

export enum TransferJobStatus {
  QUEUED = 'queued',
  ACTIVE = 'active',
  COMPLETE = 'complete',
  ERROR = 'error',
  CANCELLED = 'cancelled',
}

// ─── Shapes ───────────────────────────────────────────────────────────────────

export interface FileJob {
  fileName: string;
  status: FileJobStatus;
  /** 0–100 */
  progress: number;
  error?: string;
}

export type TransferSubject = {
  kind: 'project' | 'blueprint';
  id: string;
  label: string;
};

export interface TransferJob {
  subject?: TransferSubject;
  id: string;
  type: 'download' | 'sync' | 'create';
  projectId: string;
  projectName: string;
  status: TransferJobStatus;
  totalFiles: number;
  completedFiles: number;
  totalBytes: number;
  completedBytes: number;
  statusMessage?: string;
  fileJobs: FileJob[];
  /** Unix ms timestamp set when the job reaches a terminal state */
  completedAt?: number;
}

export interface DownloadFileRequest {
  fileId: string;
  fileName: string;
  size: number;
  fullHash: string;
}

export interface TransferUploadFileRequest {
  fileName: string;
  size: number;
  fullHash?: string;
}

export interface TransferJobLifecycleCallbacks {
  onFilesPrepared?: (files: TransferUploadFileRequest[]) => void;
  onDiagnostic?: (stage: string, error: unknown, file?: DiagnosticFile) => void;
  onContext?: (context: Partial<TransferDiagnosticContext>) => void;
  onFileStart: (fileName: string) => void;
  onStatusMessage?: (message: string) => void;
  onFileProgress?: (fileName: string, progress: number) => void;
  onFileComplete: (fileName: string) => void;
  onFileError: (fileName: string, error?: string) => void;
  getAbortSignal?: () => AbortSignal | undefined;
}

export interface DownloadJobRequest {
  subject?: TransferSubject;
  resolveDownloadUrl?: (file: DownloadFileRequest) => Promise<string>;
  version?: number;
  projectId?: string;
  projectName?: string;
  files: DownloadFileRequest[];
  dirHandle: string | FileSystemDirectoryHandle;
  /** If provided, a sub-folder with this name is created/replaced inside dirHandle */
  projectFolderName?: string;
}

export interface UploadTransferJobRequest {
  subject?: TransferSubject;
  statusMessage?: string;
  version?: number;
  projectId?: string;
  projectName?: string;
  files: TransferUploadFileRequest[];
  run: (callbacks: TransferJobLifecycleCallbacks) => Promise<void>;
  successMessage?: string;
  errorMessage?: string;
  suppressNotifications?: boolean;
}

export type SyncJobRequest = UploadTransferJobRequest;
export type CreateJobRequest = UploadTransferJobRequest;

function diagnosticSubject(
  request: Pick<
    UploadTransferJobRequest,
    'subject' | 'projectId' | 'projectName'
  >
): Partial<TransferDiagnosticContext> {
  return request.subject?.kind === 'blueprint'
    ? { blueprintId: request.subject.id, blueprintName: request.subject.label }
    : {
        projectId: request.projectId ?? request.subject?.id ?? '',
        projectName: request.subject?.label ?? request.projectName ?? '',
      };
}

// ─── Context value ─────────────────────────────────────────────────────────────

interface TransferManagerContextValue {
  queue: TransferJob[];
  activeJob: TransferJob | null;
  history: TransferJob[];
  enqueueDownload: (
    request: DownloadJobRequest
  ) => Promise<string | FileSystemDirectoryHandle | null>;
  enqueueSync: (request: SyncJobRequest) => Promise<void>;
  enqueueCreate: (request: CreateJobRequest) => Promise<void>;
  cancelJob: (id: string) => void;
  clearHistory: () => void;
}

const TransferManagerContext =
  createContext<TransferManagerContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const TransferManagerProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Active transfer queue shown in the sidebar.
  const [queue, setQueue] = useState<TransferJob[]>([]);
  // Ref mirror so async jobs always read latest queue state.
  const queueRef = useRef<TransferJob[]>([]);
  // Completed / terminal jobs persisted for History tab.
  const [history, setHistory] = useState<TransferJob[]>(() => {
    try {
      const stored = sessionStorage.getItem('transfer-history');
      return stored ? (JSON.parse(stored) as TransferJob[]) : [];
    } catch {
      return [];
    }
  });

  // Track which job IDs are already scheduled to move to history
  const scheduledForHistory = useRef(new Set<string>());
  // Serialize upload jobs at the job level (each job may still run file-level concurrency).
  const uploadWorkerRef = useRef<Promise<void>>(Promise.resolve());
  // Keep one AbortController per upload/sync job for true in-flight cancellation.
  const uploadAbortControllersRef = useRef<Map<string, AbortController>>(
    new Map()
  );
  const downloadAbortControllersRef = useRef<Map<string, AbortController>>(
    new Map()
  );
  const historyTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set()
  );
  // Serialize download jobs at the job level.
  const downloadWorkerRef = useRef<Promise<unknown>>(Promise.resolve(null));

  useEffect(() => {
    const uploadControllers = uploadAbortControllersRef.current;
    const downloadControllers = downloadAbortControllersRef.current;
    const historyTimeouts = historyTimeoutsRef.current;

    return () => {
      uploadControllers.forEach((controller) => controller.abort());
      downloadControllers.forEach((controller) => controller.abort());
      historyTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      uploadControllers.clear();
      downloadControllers.clear();
      historyTimeouts.clear();
    };
  }, []);

  const isUploadAbortError = useCallback((error: unknown): boolean => {
    return (
      error instanceof Error &&
      (error.name === 'UploadAbortError' || error.name === 'AbortError')
    );
  }, []);

  // Keep queueRef synchronized so cancellation checks inside async flows are reliable.
  useEffect(() => {
    queueRef.current = queue;

    // Clear stale controllers once jobs are terminal or removed.
    const activeUploadJobIds = new Set(
      queue
        .filter(
          (job) =>
            (job.type === 'create' || job.type === 'sync') &&
            job.status !== TransferJobStatus.COMPLETE &&
            job.status !== TransferJobStatus.ERROR &&
            job.status !== TransferJobStatus.CANCELLED
        )
        .map((job) => job.id)
    );

    uploadAbortControllersRef.current.forEach((_controller, jobId) => {
      if (!activeUploadJobIds.has(jobId)) {
        uploadAbortControllersRef.current.delete(jobId);
      }
    });
  }, [queue]);

  // Auto-move terminal jobs to history after a short delay so the user can
  // see the final COMPLETE / ERROR / CANCELLED state in the queue first.
  useEffect(() => {
    // Find terminal jobs that have not yet been scheduled for history migration.
    const terminalJobs = queue.filter(
      (j) =>
        (j.status === TransferJobStatus.COMPLETE ||
          j.status === TransferJobStatus.ERROR ||
          j.status === TransferJobStatus.CANCELLED) &&
        !scheduledForHistory.current.has(j.id)
    );
    if (terminalJobs.length === 0) return;

    // Move each terminal job after a short delay so users can see final state in Queue first.
    terminalJobs.forEach((job) => {
      scheduledForHistory.current.add(job.id);
      const timeoutId = setTimeout(() => {
        historyTimeoutsRef.current.delete(timeoutId);
        // Stamp completion time when entering history list.
        const historyEntry: TransferJob = { ...job, completedAt: Date.now() };
        setHistory((prev) => {
          const next = [historyEntry, ...prev];
          try {
            sessionStorage.setItem('transfer-history', JSON.stringify(next));
          } catch {
            // sessionStorage not available (e.g. private browsing quota)
          }
          return next;
        });
        setQueue((prev) => prev.filter((j) => j.id !== job.id));
        scheduledForHistory.current.delete(job.id);
      }, 1500);
      historyTimeoutsRef.current.add(timeoutId);
    });
  }, [queue]);

  const { showSuccess } = useNotification();
  const [fileDownload] = useFileDownloadMutation();
  const isElectron = useIsElectron();
  const electronAPI = useElectronAPI();
  const { setOpen: setSidebarOpen } = useTransferSidebar();

  // Generic queue updater helper to mutate exactly one job by id.
  const updateJob = useCallback(
    (jobId: string, updater: (job: TransferJob) => TransferJob) => {
      setQueue((prev) =>
        prev.map((job) => (job.id === jobId ? updater(job) : job))
      );
    },
    []
  );

  // ── helpers ────────────────────────────────────────────────────────────────

  // Confirm destructive replacement when target folder already exists.
  const confirmReplaceExistingFolder = (folderName: string): boolean =>
    window.confirm(
      `A folder named "${folderName}" already exists. Replace it after the download is verified? Close your DAW first. The previous folder will be kept beside it as a .backup folder. Cancel leaves it unchanged.`
    );

  // Resolve final download destination (Electron path or FileSystemHandle), including replace flow.
  const resolveWriteTarget = async (
    dirHandle: string | FileSystemDirectoryHandle,
    projectFolderName?: string
  ): Promise<{
    target: string | FileSystemDirectoryHandle;
    finish: () => Promise<string | FileSystemDirectoryHandle>;
  } | null> => {
    // No per-project folder requested: use provided handle/path as-is.
    const folderName = projectFolderName;
    if (!folderName)
      return { target: dirHandle, finish: async () => dirHandle };
    assertSafeRelativePath(folderName);
    if (folderName.includes('/'))
      throw new Error('Project folder must be a single folder name');

    // Electron path flow.
    if (typeof dirHandle === 'string') {
      const folderPath = `${dirHandle}/${folderName}`;

      // Probe folder existence through Electron API when available.
      let folderExists = false;
      if (isElectron && electronAPI?.getFileInfo) {
        const infoResult = await electronAPI.getFileInfo(folderPath);
        folderExists = !!(infoResult.success && infoResult.info?.isDirectory);
      }

      // Let user abort replacement for existing folder.
      if (folderExists && !confirmReplaceExistingFolder(folderName))
        return null;

      // Download into a sibling staging folder; do not touch existing bytes until
      // every file is verified. The native publish operation retains a backup.
      if (folderExists) {
        if (!isElectron || !electronAPI?.replaceDownloadedDirectory)
          throw new Error('Replace is not available in this desktop context');
        const staging = `${dirHandle}/.studio-vault-download-${crypto.randomUUID()}`;
        return {
          target: staging,
          finish: async () => {
            const result = await electronAPI.replaceDownloadedDirectory(
              staging,
              folderPath
            );
            if (!result.success)
              throw new Error(
                result.error || 'Failed to publish downloaded folder'
              );
            return folderPath;
          },
        };
      }

      return { target: folderPath, finish: async () => folderPath };
    }

    // Web FileSystemAPI path
    // Probe whether target sub-folder already exists.
    let existingFolder: FileSystemDirectoryHandle | null = null;
    try {
      existingFolder = await dirHandle.getDirectoryHandle(folderName);
    } catch (error) {
      if ((error as DOMException).name !== 'NotFoundError') throw error;
    }

    // Browsers lack atomic directory rename. Stage first, then retain a complete
    // backup before touching the destination; backup failure aborts replacement.
    if (existingFolder) {
      if (!confirmReplaceExistingFolder(folderName)) return null;
      const stagingName = `.studio-vault-download-${crypto.randomUUID()}`;
      const staging = await dirHandle.getDirectoryHandle(stagingName, {
        create: true,
      });
      const original = existingFolder;
      return {
        target: staging,
        finish: async () => {
          const backupName = `${folderName}.backup-${crypto.randomUUID()}`;
          const backup = await dirHandle.getDirectoryHandle(backupName, {
            create: true,
          });
          await copyDirectory(original, backup);
          try {
            await dirHandle.removeEntry(folderName, { recursive: true });
            const target = await dirHandle.getDirectoryHandle(folderName, {
              create: true,
            });
            await copyDirectory(staging, target);
            await dirHandle.removeEntry(stagingName, { recursive: true });
            return target;
          } catch {
            throw new Error(
              `Could not finish replacement. Original files are preserved in ${backupName}`
            );
          }
        },
      };
    }

    // Return writable folder handle, creating if needed.
    const target = await dirHandle.getDirectoryHandle(folderName, {
      create: true,
    });
    return { target, finish: async () => target };
  };

  // Create and enqueue an upload/sync job shell before the network work starts.
  const createUploadJob = useCallback(
    (
      type: TransferJob['type'],
      request: UploadTransferJobRequest,
      status: TransferJobStatus = TransferJobStatus.QUEUED
    ) => {
      // Stable client-side id used for queue tracking and cancellation.
      const jobId = crypto.randomUUID();
      // Sum bytes for overall progress bar.
      const totalBytes = request.files.reduce(
        (sum, file) => sum + file.size,
        0
      );

      // Initialize all file rows in pending state.
      const job: TransferJob = {
        id: jobId,
        type,
        subject: request.subject,
        projectId: request.projectId ?? '',
        projectName: request.subject?.label ?? request.projectName ?? '',
        status,
        totalFiles: request.files.length,
        completedFiles: 0,
        totalBytes,
        completedBytes: 0,
        statusMessage: 'Waiting in queue',
        fileJobs: request.files.map((file) => ({
          fileName: file.fileName,
          status: FileJobStatus.PENDING,
          progress: 0,
        })),
      };

      // Push to queue and auto-open transfer sidebar so user sees the new job.
      setQueue((prev) => [...prev, job]);
      setSidebarOpen(true);

      if (type === 'create' || type === 'sync') {
        uploadAbortControllersRef.current.set(jobId, new AbortController());
      }

      return jobId;
    },
    [setSidebarOpen]
  );

  // Read cancellation directly from queueRef to avoid stale closure values.
  const isJobCancelled = useCallback((jobId: string) => {
    return (
      queueRef.current.find((job) => job.id === jobId)?.status ===
      TransferJobStatus.CANCELLED
    );
  }, []);

  // Execute one upload/sync queue job from start to terminal state.
  const executeUploadJob = useCallback(
    async (
      jobId: string,
      type: 'sync' | 'create',
      request: UploadTransferJobRequest,
      defaultSuccessMessage: string
    ) => {
      // Respect cancellation before work begins.
      if (isJobCancelled(jobId)) return;

      // Mark job active and set initial stage message.
      updateJob(jobId, (job) => ({
        ...job,
        type,
        status: TransferJobStatus.ACTIVE,
        statusMessage:
          request.statusMessage ??
          (type === 'create' ? 'Creating Project' : 'Creating Version'),
      }));

      // Cache file sizes by name for byte-based progress aggregation.
      const diagnostics = createTransferDiagnostics(
        request.subject?.kind === 'blueprint'
          ? 'Blueprint upload'
          : type === 'create'
            ? 'Project upload'
            : 'Create version',
        {
          ...diagnosticSubject(request),
          version: request.version,
          jobId,
          files: request.files,
        }
      );
      const filesByName = new Map(
        request.files.map((file) => [file.fileName, file.size])
      );
      let preparedFiles = request.files;

      // Recompute completedBytes from per-file progress snapshot.
      const computeCompletedBytes = (fileJobs: TransferJob['fileJobs']) => {
        return fileJobs.reduce((sum, fileJob) => {
          const fileSize = filesByName.get(fileJob.fileName) ?? 0;
          const fileProgress = Math.max(
            0,
            Math.min(100, Math.round(fileJob.progress))
          );
          return sum + Math.round((fileSize * fileProgress) / 100);
        }, 0);
      };

      // Lifecycle bridge consumed by the caller's request.run implementation.
      const lifecycle: TransferJobLifecycleCallbacks = {
        onFilesPrepared: (files) => {
          preparedFiles = files;
          filesByName.clear();
          files.forEach((file) => filesByName.set(file.fileName, file.size));
          diagnostics.update({ files });
          updateJob(jobId, (job) => ({
            ...job,
            totalFiles: files.length,
            totalBytes: files.reduce((sum, file) => sum + file.size, 0),
            fileJobs: files.map((file) => ({
              fileName: file.fileName,
              status: FileJobStatus.PENDING,
              progress: 0,
            })),
          }));
        },
        onDiagnostic: diagnostics.add,
        onContext: diagnostics.update,
        getAbortSignal: () =>
          uploadAbortControllersRef.current.get(jobId)?.signal,
        // Update high-level stage labels (e.g. Creating Version, Uploading).
        onStatusMessage: (message) => {
          updateJob(jobId, (job) => {
            if (job.status === TransferJobStatus.CANCELLED) {
              return job;
            }

            return {
              ...job,
              statusMessage: message,
            };
          });
        },
        // File transitions to active.
        onFileStart: (fileName) => {
          updateJob(jobId, (job) => {
            if (job.status === TransferJobStatus.CANCELLED) {
              return job;
            }

            return {
              ...job,
              statusMessage: 'Uploading',
              fileJobs: job.fileJobs.map((fileJob) =>
                fileJob.fileName !== fileName
                  ? fileJob
                  : { ...fileJob, status: FileJobStatus.ACTIVE, progress: 1 }
              ),
            };
          });
        },
        // File incremental progress updates, with job-level byte aggregation.
        onFileProgress: (fileName, progress) => {
          const nextProgress = Math.max(0, Math.min(100, Math.round(progress)));
          updateJob(jobId, (job) => {
            if (job.status === TransferJobStatus.CANCELLED) {
              return job;
            }

            // Update only the target file row.
            const nextFileJobs = job.fileJobs.map((fileJob) =>
              fileJob.fileName !== fileName
                ? fileJob
                : {
                    ...fileJob,
                    status:
                      fileJob.status === FileJobStatus.COMPLETE
                        ? FileJobStatus.COMPLETE
                        : FileJobStatus.ACTIVE,
                    progress:
                      fileJob.status === FileJobStatus.COMPLETE
                        ? 100
                        : nextProgress,
                  }
            );

            return {
              ...job,
              fileJobs: nextFileJobs,
              completedBytes: computeCompletedBytes(nextFileJobs),
            };
          });
        },
        // File reached terminal success state.
        onFileComplete: (fileName) => {
          updateJob(jobId, (job) => {
            if (job.status === TransferJobStatus.CANCELLED) {
              return job;
            }

            // Avoid double-counting if callbacks fire redundantly.
            const currentFileJob = job.fileJobs.find(
              (fileJob) => fileJob.fileName === fileName
            );
            const alreadyComplete =
              currentFileJob?.status === FileJobStatus.COMPLETE;

            // Snapshot with this file forced to COMPLETE 100%.
            const nextFileJobs = job.fileJobs.map((fileJob) =>
              fileJob.fileName !== fileName
                ? fileJob
                : {
                    ...fileJob,
                    status: FileJobStatus.COMPLETE,
                    progress: 100,
                    error: undefined,
                  }
            );

            return {
              ...job,
              completedFiles: alreadyComplete
                ? job.completedFiles
                : job.completedFiles + 1,
              completedBytes: computeCompletedBytes(nextFileJobs),
              fileJobs: nextFileJobs,
            };
          });
        },
        // File-level failure while keeping job available for final error handling.
        onFileError: (fileName, error) => {
          diagnostics.add(
            'Upload file',
            error || 'Transfer failed',
            preparedFiles.find((file) => file.fileName === fileName) ?? {
              fileName,
            }
          );
          updateJob(jobId, (job) => {
            if (job.status === TransferJobStatus.CANCELLED) {
              return job;
            }

            return {
              ...job,
              fileJobs: job.fileJobs.map((fileJob) =>
                fileJob.fileName !== fileName
                  ? fileJob
                  : {
                      ...fileJob,
                      status: FileJobStatus.ERROR,
                      error: error || 'Transfer failed',
                    }
              ),
            };
          });
        },
      };

      try {
        // Delegate actual upload/version flow to caller-supplied run implementation.
        await request.run(lifecycle);

        if (isJobCancelled(jobId)) {
          return;
        }

        // Promote entire job to COMPLETE once run() resolves.
        updateJob(jobId, (job) => ({
          ...job,
          status: TransferJobStatus.COMPLETE,
          statusMessage: undefined,
          completedFiles: job.totalFiles,
          completedBytes: job.totalBytes,
          fileJobs: job.fileJobs.map((fileJob) =>
            fileJob.status === FileJobStatus.ERROR
              ? fileJob
              : {
                  ...fileJob,
                  status: FileJobStatus.COMPLETE,
                  progress: 100,
                }
          ),
        }));

        // Show final success toast unless caller requested silent mode.
        if (!request.suppressNotifications) {
          showSuccess(request.successMessage || defaultSuccessMessage);
        }
      } catch (error) {
        if (isUploadAbortError(error) || isJobCancelled(jobId)) {
          diagnostics.publish();
          updateJob(jobId, (job) => ({
            ...job,
            status: TransferJobStatus.CANCELLED,
            statusMessage: undefined,
          }));
          return;
        }

        // Mark job error and notify.
        diagnostics.add('Transfer failed', error);
        diagnostics.publish();
        updateJob(jobId, (job) => ({
          ...job,
          status: TransferJobStatus.ERROR,
          statusMessage: undefined,
        }));

        // The support dialog owns error presentation; a second toast can expose
        // raw details and obscure its actions, especially on small screens.
        throw error;
      } finally {
        uploadAbortControllersRef.current.delete(jobId);
      }
    },
    [isJobCancelled, isUploadAbortError, showSuccess, updateJob]
  );

  // Enqueue one upload/sync job into serialized upload job worker chain.
  const enqueueUploadJob = useCallback(
    (
      type: 'sync' | 'create',
      request: UploadTransferJobRequest,
      defaultSuccessMessage: string
    ) => {
      const jobId = createUploadJob(type, request, TransferJobStatus.QUEUED);

      // Execute this job after prior upload job settles (success or failure).
      const runTask = async () => {
        await executeUploadJob(jobId, type, request, defaultSuccessMessage);
      };

      const jobPromise = uploadWorkerRef.current.then(runTask, runTask);
      // Keep chain alive by swallowing terminal rejection at chain level.
      uploadWorkerRef.current = jobPromise.catch(() => undefined);
      return jobPromise;
    },
    [createUploadJob, executeUploadJob]
  );

  // ── enqueueDownload ────────────────────────────────────────────────────────

  const enqueueDownload = useCallback(
    async (
      request: DownloadJobRequest
    ): Promise<string | FileSystemDirectoryHandle | null> => {
      // Build queued download job model and return job id + resolved target.
      const createDownloadJob = (
        writeTarget: string | FileSystemDirectoryHandle
      ) => {
        const jobId = crypto.randomUUID();
        const totalBytes = request.files.reduce((sum, f) => sum + f.size, 0);

        const job: TransferJob = {
          id: jobId,
          type: 'download',
          subject: request.subject,
          projectId: request.projectId ?? '',
          projectName: request.subject?.label ?? request.projectName ?? '',
          status: TransferJobStatus.QUEUED,
          totalFiles: request.files.length,
          completedFiles: 0,
          totalBytes,
          completedBytes: 0,
          fileJobs: request.files.map((f) => ({
            fileName: f.fileName,
            status: FileJobStatus.PENDING,
            progress: 0,
          })),
        };

        setQueue((prev) => [...prev, job]);
        setSidebarOpen(true);
        downloadAbortControllersRef.current.set(jobId, new AbortController());

        return { jobId, writeTarget };
      };

      // Execute one download queue job end-to-end.
      const executeDownloadJob = async (
        jobId: string,
        writeTarget: string | FileSystemDirectoryHandle,
        finish: () => Promise<string | FileSystemDirectoryHandle>
      ) => {
        const diagnostics = createTransferDiagnostics(
          request.subject?.kind === 'blueprint'
            ? 'Blueprint download'
            : 'Download',
          {
            ...diagnosticSubject(request),
            version: request.version,
            jobId,
            files: request.files,
            destinationKind:
              typeof writeTarget === 'string'
                ? 'Native folder'
                : 'Browser folder',
          }
        );
        const jobAbortController =
          downloadAbortControllersRef.current.get(jobId);
        // Respect cancellation before work begins.
        if (
          !jobAbortController ||
          jobAbortController.signal.aborted ||
          isJobCancelled(jobId)
        )
          return null;

        // Mark job active.
        updateJob(jobId, (job) => ({
          ...job,
          status: TransferJobStatus.ACTIVE,
        }));

        try {
          // ── per-file download ─────────────────────────────────────────────────

          // Download one file, stream progress, and write to destination.
          const downloadSingleFile = async (
            file: DownloadFileRequest
          ): Promise<void> => {
            const throwIfCancelled = () => {
              if (jobAbortController.signal.aborted || isJobCancelled(jobId)) {
                const abortError = new Error('Download cancelled');
                abortError.name = 'AbortError';
                throw abortError;
              }
            };
            throwIfCancelled();

            // Mark file active in queue row.
            setQueue((prev) =>
              prev.map((j) =>
                j.id !== jobId
                  ? j
                  : {
                      ...j,
                      fileJobs: j.fileJobs.map((fj) =>
                        fj.fileName !== file.fileName
                          ? fj
                          : { ...fj, status: FileJobStatus.ACTIVE }
                      ),
                    }
              )
            );

            // Request presigned download URL from API.
            const url = request.resolveDownloadUrl
              ? await request.resolveDownloadUrl(file)
              : (
                  await fileDownload({
                    variables: {
                      data: {
                        projectId: request.projectId!,
                        fileId: file.fileId,
                      },
                    },
                  })
                ).data?.fileDownload?.presignedUrl;
            if (!url) throw new Error('Failed to get download URL');
            const response = await fetch(url, {
              signal: jobAbortController.signal,
            });
            if (!response.ok)
              throw new Error(`HTTP error! status: ${response.status}`);

            const reader = response.body?.getReader();
            const contentLength = parseInt(
              response.headers.get('Content-Length') || '0'
            );
            if (!reader)
              throw new Error('Failed to initialize download stream');

            let receivedLength = 0;
            const chunks: Uint8Array[] = [];

            while (true) {
              throwIfCancelled();
              // Read one stream chunk and update per-file + aggregate progress.
              const { done, value } = await reader.read();
              if (done) break;

              chunks.push(value);
              receivedLength += value.length;
              const fileProgress = Math.round(
                (receivedLength / contentLength) * 100
              );

              setQueue((prev) =>
                prev.map((j) =>
                  j.id !== jobId
                    ? j
                    : {
                        ...j,
                        completedBytes: j.completedBytes + value.length,
                        fileJobs: j.fileJobs.map((fj) =>
                          fj.fileName !== file.fileName
                            ? fj
                            : { ...fj, progress: fileProgress }
                        ),
                      }
                )
              );
            }

            // Rebuild blob from chunks and write to the chosen target.
            throwIfCancelled();
            const blob = new Blob(chunks as BlobPart[]);
            await verifyDownloadedFile(blob, {
              ...file,
              fullHash: file.fullHash ?? '',
            });
            throwIfCancelled();
            if (typeof writeTarget === 'string') {
              if (!isElectron || !electronAPI)
                throw new Error('Electron API not available for file download');
              await writeFileInElectron(
                electronAPI,
                `${writeTarget}/${file.fileName}`,
                blob,
                request.subject?.kind === 'blueprint'
              );
            } else {
              await writeFileInDirectory(file.fileName, blob, writeTarget);
            }

            // Mark file row complete.
            setQueue((prev) =>
              prev.map((j) =>
                j.id !== jobId
                  ? j
                  : {
                      ...j,
                      completedFiles: j.completedFiles + 1,
                      fileJobs: j.fileJobs.map((fj) =>
                        fj.fileName !== file.fileName
                          ? fj
                          : {
                              ...fj,
                              status: FileJobStatus.COMPLETE,
                              progress: 100,
                            }
                      ),
                    }
              )
            );
          };

          // ── concurrency loop (max 10 simultaneous) ────────────────────────────

          // Worker-pool style loop over pending files.
          const pending = [...request.files];
          const active = new Set<Promise<void>>();
          const MAX_CONCURRENT = 10;
          const failedFiles: string[] = [];

          while (true) {
            if (isJobCancelled(jobId)) {
              pending.length = 0;
            }
            // Fill available worker slots.
            while (active.size < MAX_CONCURRENT && pending.length > 0) {
              const file = pending.shift()!;
              const downloadPromise = downloadSingleFile(file)
                .catch((err) => {
                  if (isUploadAbortError(err) || isJobCancelled(jobId)) {
                    return;
                  }
                  // Keep processing other files while recording this one as failed.
                  failedFiles.push(file.fileName);
                  diagnostics.add('Download / verify / write file', err, file);
                  setQueue((prev) =>
                    prev.map((j) =>
                      j.id !== jobId
                        ? j
                        : {
                            ...j,
                            fileJobs: j.fileJobs.map((fj) =>
                              fj.fileName !== file.fileName
                                ? fj
                                : {
                                    ...fj,
                                    status: FileJobStatus.ERROR,
                                    error:
                                      err instanceof Error
                                        ? err.message
                                        : 'Download failed',
                                  }
                            ),
                          }
                    )
                  );
                })
                .finally(() => {
                  // Release worker slot when this file settles.
                  active.delete(downloadPromise);
                });

              active.add(downloadPromise);
            }

            // Wait for one active worker to settle, then refill.
            if (active.size > 0) await Promise.race(active);
            // Exit when no pending and no active work remains.
            if (pending.length === 0 && active.size === 0) break;
          }

          if (jobAbortController.signal.aborted || isJobCancelled(jobId))
            return null;

          // Brief pause so the final 100% state is visible
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Finalize job status and notification based on per-file failures.
          if (failedFiles.length > 0) {
            diagnostics.publish();
            setQueue((prev) =>
              prev.map((j) =>
                j.id === jobId ? { ...j, status: TransferJobStatus.ERROR } : j
              )
            );
            return null;
          } else {
            if (jobAbortController.signal.aborted || isJobCancelled(jobId))
              return null;
            const destination = await finish();
            showSuccess('Files downloaded successfully!');
            setQueue((prev) =>
              prev.map((j) =>
                j.id === jobId
                  ? { ...j, status: TransferJobStatus.COMPLETE }
                  : j
              )
            );
            return destination;
          }
        } catch (error) {
          if (
            isUploadAbortError(error) ||
            jobAbortController.signal.aborted ||
            isJobCancelled(jobId)
          ) {
            updateJob(jobId, (job) => ({
              ...job,
              status: TransferJobStatus.CANCELLED,
            }));
            return null;
          }
          // Unexpected job-level error path.
          diagnostics.add('Download / publish destination', error);
          diagnostics.publish();
          console.error('Error downloading files:', error);
          updateJob(jobId, (job) => ({
            ...job,
            status: TransferJobStatus.ERROR,
          }));
          return null;
        } finally {
          downloadAbortControllersRef.current.delete(jobId);
        }
      };

      try {
        if (!request.resolveDownloadUrl && !request.projectId)
          throw new Error('A download source is required');
        const paths = new Set<string>();
        for (const file of request.files) {
          assertSafeRelativePath(file.fileName);
          if (paths.has(file.fileName))
            throw new Error('Duplicate download file path');
          paths.add(file.fileName);
        }
        // Resolve destination path/handle, including optional replace flow.
        const resolved = await resolveWriteTarget(
          request.dirHandle,
          request.projectFolderName
        );
        if (!resolved) return null;
        const writeTarget = resolved.target;

        // Create queue job now that destination is confirmed.
        const { jobId } = createDownloadJob(writeTarget);

        // Schedule this download job in serialized download chain.
        const runTask = async () => {
          return executeDownloadJob(jobId, writeTarget, resolved.finish);
        };

        const jobPromise = downloadWorkerRef.current.then(runTask, runTask);
        // Keep chain alive regardless of this job outcome.
        downloadWorkerRef.current = jobPromise.catch(() => null);
        return await jobPromise;
      } catch (error) {
        // Errors before job creation (target resolution, setup, etc.).
        reportTransferError('Prepare download', error, {
          ...diagnosticSubject(request),
          version: request.version,
          files: request.files,
        });
        console.error('Error preparing download files:', error);
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [electronAPI, fileDownload, isElectron, setSidebarOpen, showSuccess]
  );

  // Public sync enqueue API (shares upload infrastructure).
  const enqueueSync = useCallback(
    async (request: SyncJobRequest) => {
      await enqueueUploadJob('sync', request, 'Project synced successfully!');
    },
    [enqueueUploadJob]
  );

  // Public create/upload enqueue API (shares upload infrastructure).
  const enqueueCreate = useCallback(
    async (request: CreateJobRequest) => {
      await enqueueUploadJob(
        'create',
        request,
        'Project uploaded successfully!'
      );
    },
    [enqueueUploadJob]
  );

  // ── cancelJob ──────────────────────────────────────────────────────────────

  // Mark a non-terminal job as cancelled.
  const cancelJob = useCallback((id: string) => {
    uploadAbortControllersRef.current.get(id)?.abort();
    downloadAbortControllersRef.current.get(id)?.abort();

    setQueue((prev) =>
      prev.map((j) =>
        j.id === id &&
        j.status !== TransferJobStatus.COMPLETE &&
        j.status !== TransferJobStatus.CANCELLED
          ? { ...j, status: TransferJobStatus.CANCELLED }
          : j
      )
    );
  }, []);

  // Clear persisted transfer history list.
  const clearHistory = useCallback(() => {
    setHistory([]);
    try {
      sessionStorage.removeItem('transfer-history');
    } catch {
      // ignore
    }
  }, []);

  // Expose currently active job (first active in queue order).
  const activeJob =
    queue.find((j) => j.status === TransferJobStatus.ACTIVE) ?? null;

  // Provide transfer queue state + actions to the app tree.
  return (
    <TransferManagerContext.Provider
      value={{
        queue,
        activeJob,
        history,
        enqueueDownload,
        enqueueSync,
        enqueueCreate,
        cancelJob,
        clearHistory,
      }}
    >
      {children}
    </TransferManagerContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

// Convenience hook with provider guard.
export const useTransferManagerContext = () => {
  const ctx = useContext(TransferManagerContext);
  if (!ctx)
    throw new Error(
      'useTransferManagerContext must be used within a TransferManagerProvider'
    );
  return ctx;
};
